import React, { useState, useEffect, useRef, useMemo } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

const MessageInput = ({ message, setMessage }) => {
  const iconToolbox = [
    '😀', '😁', '😎', '🥳', '👏', '🙌', '👍', '💪', '✅', '🔥', '🎯', '🌟', '✨', '🚀',
    '💡', '🎉', '🎬', '🎵', '📷', '📹', '🧠', '📝', '📌', '📣', '💬', '❤️', '🤩'
  ];

  const insertIcon = (icon) => {
    setMessage((prev) => prev + ' ' + icon);
  };

  return (
    <div className="w-100">


      <textarea
        rows={2}
        className="w-100"
        style={{
          width: '100%',
          fontSize: '1rem',
          padding: '1rem',
          border: '1px solid #ccc',
          borderRadius: '8px',
          resize: 'vertical',
          background: '#f9f9f9',
        }}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Ajoutez un message pour les contributeurs (ex. bravo, prochaine étape, etc.)"
      />
      <div className="flex flex-wrap gap-3 mb-3">
        {iconToolbox.map((icon, idx) => (
          <span
            key={idx}
            onClick={() => insertIcon(icon)}
            className="text-2xl cursor-pointer hover:scale-110 transition-transform"
            style={{ border: 'none', background: 'transparent' }}
          >
            {icon}
          </span>
        ))}
      </div>
    </div>
  );
};

const ProjectRecap = ({ project, finalVideoUrl }) => {
  const [showSteps, setShowSteps] = useState(false);
  const [showMsgs, setShowMsgs] = useState(false);
  const [messages, setMessages] = useState([]);
  const messagesEndRef = useRef(null);
  const [userMessage, setUserMessage] = useState('');
  const fileInputRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [shareTitle, setShareTitle] = useState(project.title || '');
  const [shareDescription, setShareDescription] = useState(project.description || '');
  const [isPublishing, setIsPublishing] = useState(false);
  const [youtubeVideoId, setYoutubeVideoId] = useState(project.youtube_video_id || null);
  const [youtubeStats, setYoutubeStats] = useState(null);
  const groupedByContributor = {};
  const [revenue, setRevenue] = useState(null);
  const sampleRealtimeData = useMemo(() => [
    { time: '-60 min', views: 0 },
    { time: '-45 min', views: 1 },
    { time: '-30 min', views: 2 },
    { time: '-15 min', views: 1 },
    { time: 'Maintenant', views: 3 },
  ], []);
  const realtimeViews = useMemo(() => [
    { time: '-60 min', views: 0 },
    { time: '-45 min', views: 2 },
    { time: '-30 min', views: 4 },
    { time: '-15 min', views: 5 },
    { time: 'Maintenant', views: 7 },
  ], []);
  
  const dailyLikes = useMemo(() => [
    { day: 'Lundi', likes: 3 },
    { day: 'Mardi', likes: 6 },
    { day: 'Mercredi', likes: 10 },
    { day: 'Jeudi', likes: 14 },
    { day: 'Vendredi', likes: 20 },
    { day: 'Samedi', likes: 25 },
    { day: 'Aujourd’hui', likes: 31 },
  ], []);
  
  const dailyComments = useMemo(() => [
    { day: 'Lundi', comments: 0 },
    { day: 'Mardi', comments: 1 },
    { day: 'Mercredi', comments: 1 },
    { day: 'Jeudi', comments: 2 },
    { day: 'Vendredi', comments: 3 },
    { day: 'Samedi', comments: 4 },
    { day: 'Aujourd’hui', comments: 6 },
  ], []);

useEffect(() => {
  const loadShareAndStats = async () => {
    try {
      console.log("📥 Chargement des infos de partage...");
      const res = await fetch(`/projects/${project.id}/share-data`);
      if (!res.ok) throw new Error('Pas encore partagé');

      const data = await res.json();
      console.log("✅ Partage récupéré depuis DB:", data);

      // 🔁 Mise à jour des stats depuis l'API YouTube
      await fetch(`/youtube-stats/${data.video_id}`);
      console.log("📡 Stats mises à jour via API YouTube");

      // 🔁 Recharge les stats depuis la base après la mise à jour
      const refreshed = await fetch(`/projects/${project.id}/share-data`);
      const updated = await refreshed.json();
      console.log("🧩 Stats rechargées depuis DB :", updated.view_count, updated.like_count, updated.comment_count);

      // ✅ Mise à jour des états
      setYoutubeVideoId(updated.video_id);
      setShareTitle(updated.title);
      setShareDescription(updated.description);
      const newStats = {
        viewCount: updated.view_count,
        likeCount: updated.like_count,
        commentCount: updated.comment_count
      };
      console.log("🧪 setYoutubeStats with:", newStats);
      setYoutubeStats(newStats);


    } catch (err) {
      console.log("ℹ️ Aucun partage disponible ou erreur :", err.message);
    }
  };

  if (project?.id) {
    loadShareAndStats();
  }
}, [project.id]);

  

  project.media.forEach((item) => {
    const contributor = item.contributor || 'Anonyme';
    if (!groupedByContributor[contributor]) groupedByContributor[contributor] = [];
    groupedByContributor[contributor].push(item);
  });

  return (
    <div className="container my-5">
      <div className="text-center mb-5">
        <h2 className="text-primary fw-bold">📽️ {project.title}</h2>
        <p className="text-muted">{project.description}</p>
        <span className="badge bg-secondary px-3 py-2 rounded-pill">Projet clôturé</span>
      </div>

      {/* 🎞️ Vidéo finale */}
      <div className="text-center mb-5">
        <video
          src={finalVideoUrl}
          controls
          className="w-100 rounded shadow"
          style={{ maxWidth: "720px", maxHeight: "400px", objectFit: "cover" }}
        />
        <p className="mt-2 text-muted">🎬 Vidéo générée avec IA</p>
        
        {!youtubeVideoId ? (
          <div className="bg-white p-4 rounded shadow-sm mt-4">
            <h5 className="mb-3">📤 Publier sur YouTube</h5>

            <div className="mb-2">
              <label className="form-label">🎬 Titre</label>
              <input
                type="text"
                className="form-control"
                value={shareTitle}
                onChange={(e) => setShareTitle(e.target.value)}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">📝 Description</label>
              <textarea
                className="form-control"
                rows={3}
                value={shareDescription}
                onChange={(e) => setShareDescription(e.target.value)}
              />
            </div>

            <button
  className="btn btn-danger"
  disabled={isPublishing}
  onClick={async () => {
    setIsPublishing(true);
    try {
      const res = await fetch('/youtube-upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: project.id,
          title: shareTitle,
          description: shareDescription
        })
      });

      const data = await res.json();

      if (data.video_id) {
        // Rafraîchir les stats dans la base
        const statsRes = await fetch(`/youtube-stats/${data.video_id}`);
        if (statsRes.ok) {
          const stats = await statsRes.json();
          console.log("📊 Stats mises à jour :", stats);
        }

        // ✅ Recharger la page pour afficher les stats depuis la base
        window.location.reload(); // ← Forcer la réinitialisation des useEffect
      }
    } catch (err) {
      console.error(err);
      alert("❌ Erreur réseau ou serveur");
    } finally {
      setIsPublishing(false);
    }
  }}
>
  {isPublishing ? "⏳ Publication en cours…" : "📤 Publier sur YouTube"}
</button>

          </div>
        ) : (
          <div className="mt-5 p-4 bg-white rounded shadow-sm border">
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h5 className="mb-0 d-flex align-items-center gap-2">
              <span style={{ fontSize: "1.5rem", color: "#FF0000" }}>📺</span>
              Statistiques YouTube
            </h5>
            <a
              href={`https://www.youtube.com/watch?v=${youtubeVideoId}`}
              target="_blank"
              rel="noreferrer"
              className="btn btn-outline-primary btn-sm"
            >
              ▶️ Voir sur YouTube
            </a>
          
          </div>
      
          <div className="row g-4">
            {/* Miniature vidéo */}
            <div className="col-md-4">
              <div className="ratio ratio-16x9 border rounded shadow-sm">
                <iframe
                  src={`https://www.youtube.com/embed/${youtubeVideoId}`}
                  title="YouTube video"
                  frameBorder="0"
                  allowFullScreen
                />
              </div>
              <div className="mt-2 text-muted small">🎞️ Aperçu intégré</div>
            </div>
      
            {/* Stats + détails */}
            <div className="col-md-8">
  <div className="bg-white rounded shadow-sm p-4 border h-100">
  
    <div className="mb-4">
  <h6 className="text-uppercase text-muted fw-bold mb-3">📄 Détails ( data)</h6>

  <div className="mb-3">
    <label className="fw-semibold text-secondary d-block small mb-1">🎬 Titre</label>
    <div className="border rounded p-2 bg-light text-dark">
      {shareTitle || <em className="text-muted">Non renseigné</em>}
    </div>
  </div>

  <div>
    <label className="fw-semibold text-secondary d-block small mb-1">📝 Description</label>
    <div className="border rounded p-2 bg-light text-dark" style={{ whiteSpace: 'pre-wrap' }}>
      {shareDescription || <em className="text-muted">Aucune description fournie</em>}
    </div>
  </div>
</div>

    <h6 className="text-uppercase text-muted fw-bold mb-3 mt-4">📊 Statistiques</h6>
<div className="row g-3">
  <div className="col-12 col-sm-4">
    <div className="bg-light rounded shadow-sm p-3 text-center">
      <div className="h4 text-primary mb-0">
      {typeof youtubeStats?.viewCount === 'number' ? youtubeStats.viewCount : '⏳'}
    </div>
      <div className="small text-muted mt-1">👁️ Vues</div>
    </div>
  </div>
  <div className="col-12 col-sm-4">
    <div className="bg-light rounded shadow-sm p-3 text-center">
      <div className="h4 text-success mb-0">
      {typeof youtubeStats?.likeCount === 'number' ? youtubeStats.likeCount : '⏳'}
    </div>
      <div className="small text-muted mt-1">👍 Likes</div>
    </div>
  </div>
  <div className="col-12 col-sm-4">
    <div className="bg-light rounded shadow-sm p-3 text-center">
      <div className="h4 text-warning mb-0">
      {typeof youtubeStats?.commentCount === 'number' ? youtubeStats.commentCount : '⏳'}
    </div>
      <div className="small text-muted mt-1">💬 Commentaires</div>
    </div>
  </div>
</div>
    </div>
 </div> 
</div>
          <div className="mt-4">
        <h6 className="text-secondary mb-2">📈 Vue en temps réel</h6>
        <div className="bg-light rounded p-3 shadow-sm" style={{ height: 250 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={sampleRealtimeData}>
              <XAxis dataKey="time" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="views" stroke="#FF0000" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-5">
  <h6 className="text-secondary mb-3">📊 YouTube Analytics (simulé)</h6>
  
  <div className="row g-4">
    

    <div className="col-md-6">
      <div className="bg-light rounded shadow-sm p-3 h-100">
        <h6 className="mb-2">👍 Likes par jour</h6>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={dailyLikes}>
            <XAxis dataKey="day" />
            <YAxis />
            <Tooltip />
            <Line type="monotone" dataKey="likes" stroke="#1E90FF" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>

    <div className="col-md-6">
      <div className="bg-light rounded shadow-sm p-3 h-100">
        <h6 className="mb-2">💬 Commentaires par jour</h6>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={dailyComments}>
            <XAxis dataKey="day" />
            <YAxis />
            <Tooltip />
            <Line type="monotone" dataKey="comments" stroke="#00C49F" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  <div className="mt-3 alert alert-info">
    💰 Revenu estimé (mai 2024)  : <strong>{revenue} 1344€ *</strong>
    <div>* Simulated: Account must be in the YouTube Partner Program and monetization is enabled</div>
  </div>
  </div>
</div>

      </div>
        </div>
        
        )}
      </div>
      
  <br></br>


      {/* 📝 Scénario */}
      {project.scenario && (
        <div className="bg-light p-4 rounded shadow-sm mb-5">
          <h5 className="text-dark mb-3">📝 Scénario</h5>
          <p className="text-muted">Parcours défini en plusieurs étapes créatives.</p>

          <button
            className="btn btn-sm btn-outline-secondary mb-3"
            onClick={() => setShowSteps(!showSteps)}
          >
            {showSteps ? '🔽 Masquer les étapes' : '▶️ Afficher les étapes'}
          </button>

          {showSteps && (
            <div className="mt-3">
              <TimelineSteps steps={JSON.parse(project.scenario.steps || '[]')} />
            </div>
          )}
        </div>
      )}
      {project.messages && (
        <div className="bg-light p-4 rounded shadow-sm mb-5">
          <h5 className="text-dark mb-3">📝 Forum</h5>
          <p className="text-muted">vue sur les messages partagés </p>

          <button
            className="btn btn-sm btn-outline-secondary mb-3"
            onClick={() => setShowMsgs(!showMsgs)}
          >
            {showMsgs ? '🔽 Masquer les messages' : '▶️ Afficher les messages'}
          </button>

          {showMsgs && (
            <div className="chat-wrapper container p-0">
              <h5 className="my-3">💬 Forum du documentaire</h5>

              <div className="chat-box border rounded bg-light p-3 d-flex flex-column" style={{ height: 'auto' }}>
                <div className="messages-list flex-grow-1 overflow-auto pe-2 mb-2">
                  {project.messages
                    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
                    .map((msg) => {
                      const gender = msg.id % 2 === 0 ? 'men' : 'women';
                      const imgId = msg.id % 100;
                      const fakeNames = [
                        'Claire P.', 'Thomas G.', 'Sofia L.', 'Antoine D.', 'Inès K.',
                        'Lucas M.', 'Emma B.', 'Nicolas V.', 'Leila T.', 'Hugo J.'
                      ];
                      const displayName = msg.username || fakeNames[msg.id % fakeNames.length];

                      // Gestion média joint
                      const fileUrl = msg.file_url;
                      const isImage = fileUrl?.match(/\.(jpeg|jpg|png|gif)$/i);
                      const isVideo = fileUrl?.match(/\.(mp4|webm|ogg)$/i);

                      return (
                        <div
                          key={msg.id}
                          className="chat-message p-2 border rounded bg-white position-relative mb-3"
                        >
                          <div className="d-flex align-items-center mb-1">
                            <img
                              src={`https://randomuser.me/api/portraits/${gender}/${imgId}.jpg`}
                              alt="user"
                              className="rounded-circle me-2"
                              style={{ width: '36px', height: '36px', objectFit: 'cover' }}
                            />
                            <strong className="me-2">{displayName}</strong>
                            <small className="text-muted">
                              🕒 {new Date(msg.created_at).toLocaleString()}
                            </small>
                          </div>

                          <div className="ps-5 mb-1">{msg.message}</div>

                          {fileUrl && (

                            <div className="ps-5">
                              {isImage && (

                                <img
                                  src={`/uploads/${fileUrl}`}
                                  alt="attachment"
                                  className="img-fluid rounded border"
                                  style={{ maxWidth: '300px' }}
                                />
                              )}
                              {isVideo && (
                                <video
                                  src={`/uploads/${fileUrl}`}
                                  controls
                                  className="rounded border"
                                  style={{ maxWidth: '300px' }}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  <div ref={messagesEndRef} />
                </div>


              </div>
            </div>
          )}
        </div>
      )}

      {/* 🙋‍♂️ Contributeurs */}
      <div className="mb-5">
        <h5 className="mb-3">🙋‍♂️ Contributeurs & Médias</h5>

        {Object.entries(groupedByContributor).map(([contributor, medias]) => (
          <div key={contributor} className="mb-4 p-3 bg-white shadow-sm ">
            <h6 className="fw-bold text-primary mb-2">🎁 Merci {contributor} pour votre contribution!</h6>
            <div className="d-flex flex-wrap gap-3">
              {medias.map((item) => {
                 const mediaUrl = `https://www.weavevo.com/uploads/${item.file_path}`;
                
                const isVideo = item.file_path.match(/\.(mp4|webm|ogg)$/i);
                return (
                  <div
                    key={item.id}
                    className="rounded border overflow-hidden shadow-sm"
                    style={{ width: 160, height: 120 }}
                  >
                    {isVideo ? (
                      <video src={mediaUrl} className="w-100 h-100" controls />
                    ) : (
                      <img src={mediaUrl} alt={item.title} className="w-100 h-100 object-fit-cover" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* 💌 Message final */}
      {project.closing_message && (
        <div className="p-4 bg-white border-start border-4 border-success rounded shadow-sm mb-5">
          <h5 className="text-success mb-2">💌 Message de clôture</h5>
          <p className="fst-italic">{project.closing_message}</p>
        </div>
      )}

      {/* 📱 QR + partage */}
      <div className="text-center">
        <h6>📱 Regarder ou partager</h6>
        <QRCodeCanvas value={finalVideoUrl} size={128} className="my-3" />
        <div className="d-flex flex-wrap justify-content-center gap-3 mt-3">
          <a href={finalVideoUrl} className="btn btn-outline-success" target="_blank" rel="noopener noreferrer">🎬 Visionner</a>
          <a href={finalVideoUrl} download className="btn btn-outline-secondary">📥 Télécharger</a>
          <a
            href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(finalVideoUrl)}&text=🎥 Découvrez ce documentaire !`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-outline-info"
          >
            🐦 Partager
          </a>
        </div>
      </div>

      <div className="text-center mt-5">
        <hr />
        <p className="text-muted small">Merci à tous les participants pour leur contribution précieuse 🙏</p>
        <a href="/dashboard" className="btn btn-outline-primary mt-2">⬅️ Retour au tableau de bord</a>
      </div>
    </div>
  );
};

const TimelineSteps = ({ steps }) => (
  <div className="timeline">
    {steps.map((step, index) => (
      <div key={index} className="timeline-step position-relative ps-4 mb-5 fade-in">
        <div className="timeline-dot bg-primary position-absolute top-0 start-0 translate-middle rounded-circle" style={{ width: 16, height: 16 }}></div>
        <div className="bg-light-subtle p-4 rounded-3 shadow-sm border-start border-4 border-primary">
          <h5 className="mb-2 text-primary d-flex align-items-center gap-2">📍 {step.location}</h5>
          <p className="text-dark mb-2 fst-italic">{step.description}</p>
          {step.reference && (
            <p className="text-muted small mb-0">
              🔗 <a href={step.reference} target="_blank" rel="noreferrer">Voir la ressource</a>
            </p>
          )}
        </div>
      </div>
    ))}
  </div>
);

export default ProjectRecap;
